import { Modal2 } from "@iq/models";
import { useEffect, useMemo, useState } from "react";
import { resolveAppointmentDateKey } from "../Calendar/Calendar";
import ServiceBuilder from "../ServiceBuilder/ServiceBuilder";
import { getProducts } from "../../services/OnlineStoreService";
import { addTipToAppointment, editAppointment, getAppointmentPaymentTotal } from "../../services/AppointmentService";
import { formatPrice } from "../../utils/pricing";
import { formatTime } from "../../utils/time";
import {
    canSaveSelections,
    getProductAttributes,
    getSelectedServiceDetails,
    restoreSelections,
} from "../../utils/serviceBuilder";
import TipStep from "./TipStep";
import { appointmentServices, discountedSubtotal, isTipValid, serviceKey, tipCents } from "./completion";
import styles from "./completeAppointment.module.css";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

const DAY_MS = 24 * 60 * 60 * 1000;
const DONE_MESSAGES = {
    card: "Payment collected and appointment completed.",
    cash: "Appointment completed with cash payment.",
    cancel: "Appointment cancelled.",
};

function appointmentStart(appointment) {
    const dateKey = resolveAppointmentDateKey(appointment);
    const time = String(appointment?.start_time || "00:00").slice(0, 5);
    return dateKey ? new Date(`${dateKey}T${time}:00`) : null;
}

function buildSteps(mode, serviceCount) {
    if (!mode) return [];
    if (mode === "cancel") return [{ type: "review", label: "Review" }];

    const serviceSteps = Array.from({ length: serviceCount }, (_, index) => ({
        type: "service",
        index,
        label: serviceCount > 1 ? `Service ${index + 1}` : "Options",
    }));
    const tipStep = mode === "card" ? [{ type: "tip", label: "Tip" }] : [];
    return [...serviceSteps, ...tipStep, { type: "review", label: "Review" }];
}

function selectionSignature(selection = {}) {
    return JSON.stringify(Object.entries(selection).sort(([first], [second]) => first.localeCompare(second)));
}

function editErrorMessage(requestError) {
    const overlaps = requestError?.payload?.overlaps;
    if (requestError?.status !== 409 || !Array.isArray(overlaps) || overlaps.length === 0) {
        return requestError.message;
    }

    const details = overlaps.map((overlap) => {
        const customer = overlap.customer_name || "another customer";
        return `${customer} on ${overlap.date} from ${overlap.start_time} to ${overlap.end_time} (${overlap.overlap_minutes} minutes)`;
    });
    return `Changes not saved. This appointment would overlap ${details.join(", ")}.`;
}

function CompleteAppointmentModal({ isOpen, appointment, onClose, onSubmit, onCompleted }) {
    const [mode, setMode] = useState(null);
    const [step, setStep] = useState(0);
    const [products, setProducts] = useState({});
    const [selections, setSelections] = useState({});
    const [initialSelections, setInitialSelections] = useState({});
    const [editedServiceKeys, setEditedServiceKeys] = useState(() => new Set());
    const [tip, setTip] = useState(null);
    const [savedTip, setSavedTip] = useState(0);
    const [deposit, setDeposit] = useState(null);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [savingTip, setSavingTip] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const services = useMemo(() => appointmentServices(appointment), [appointment]);

    useEffect(() => {
        if (!isOpen) return;

        let active = true;
        setMode(null);
        setStep(0);
        setTip(null);
        setSavedTip(0);
        setDeposit(null);
        setDiscountPercentage(0);
        setDone(false);
        setError("");
        setSelections({});
        setInitialSelections({});
        setEditedServiceKeys(new Set());
        setLoading(true);

        Promise.all([getProducts(), getAppointmentPaymentTotal(appointment.id)])
            .then(([productList, paymentTotal]) => {
                if (!active) return;
                const paidDeposit = paymentTotal?.deposit_paid_amount;
                const percentage = paymentTotal?.discount_percentage ?? (appointment.discount_id ? null : 0);
                discountedSubtotal(0, percentage);
                if (!Number.isSafeInteger(paidDeposit) || paidDeposit < 0) {
                    throw new Error("The paid deposit could not be verified. Reconcile the appointment before completion.");
                }
                const productsByID = Object.fromEntries(productList.map((product) => [Number(product.id), product]));
                setProducts(productsByID);
                const restoredSelections = Object.fromEntries(services.map((service, index) => [
                    serviceKey(service, index),
                    restoreSelections(productsByID[Number(service.product_id)], service.attributes),
                ]));
                setSelections(restoredSelections);
                setInitialSelections(restoredSelections);
                setDeposit(paidDeposit);
                setDiscountPercentage(percentage);
                const storedTip = paymentTotal?.tip_amount ?? 0;
                if (!Number.isSafeInteger(storedTip) || storedTip < 0) {
                    setDeposit(null);
                    throw new Error("The saved tip could not be verified.");
                }
                setSavedTip(storedTip);
            })
            .catch((requestError) => {
                if (active) setError(requestError.message);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [isOpen, appointment?.id, appointment?.discount_id, services]);

    const pricedServices = useMemo(() => services.map((service, index) => {
        const key = serviceKey(service, index);
        const product = products[Number(service.product_id)];
        const selection = selections[key] || {};
        const bookedValuePrices = new Map((service.attributes || []).map((attribute) => [Number(attribute.attribute_value_id), Number(attribute.price || 0)]));
        const pricingOverrides = editedServiceKeys.has(key)
            ? {}
            : { basePrice: service.base_price, valuePrices: bookedValuePrices };
        const details = getSelectedServiceDetails(product, selection, pricingOverrides);

        return {
            key,
            service,
            product,
            selection,
            name: service.name || product?.name || "Service",
            price: details ? details.total_price : Number(service.total_price || 0),
            labels: details
                ? details.selected_attributes.map((attribute) => `${attribute.attribute_name}: ${attribute.attribute_value}`)
                : (service.attributes || []).map((attribute) => `${attribute.name}: ${attribute.value}`),
            valueIDs: details
                ? details.selected_attributes.map((attribute) => attribute.attribute_value_id)
                : (service.attributes || []).map((attribute) => Number(attribute.attribute_value_id)),
            attributeSelection: details?.attribute_selection || {},
            complete: !product || canSaveSelections(getProductAttributes(product), selection),
        };
    }), [services, products, selections, editedServiceKeys]);

    const startAt = useMemo(() => appointmentStart(appointment), [appointment]);
    const canCancel = Boolean(startAt) && startAt.getTime() - Date.now() > DAY_MS;

    const originalTotal = discountedSubtotal(services.reduce((sum, service) => sum + Number(service.total_price || 0), 0), discountPercentage);
    const subtotal = pricedServices.reduce((sum, item) => sum + item.price, 0);
    const total = discountedSubtotal(subtotal, discountPercentage);
    const tipAmount = mode === "card" && tip !== null ? tipCents(tip, total) : savedTip;
    const remaining = Math.max(total - Number(deposit || 0), 0);
    const overpayment = Math.max(Number(deposit || 0) - total - tipAmount, 0);
    const amountDue = Math.max(total + tipAmount - Number(deposit || 0), 0);

    const steps = buildSteps(mode, services.length);
    const currentStep = steps[step];
    const currentService = currentStep?.type === "service" ? pricedServices[currentStep.index] : null;

    function chooseMode(nextMode) {
        setMode(nextMode);
        setStep(0);
        setTip(null);
        setError("");
    }

    function goBack() {
        setError("");
        if (step === 0) setMode(null);
        else setStep((current) => current - 1);
    }

    function handleClose() {
        if (!submitting && !savingEdit && !savingTip) onClose();
    }

    async function saveServiceAndContinue() {
        if (!currentService) return;

        const { key, selection, service, attributeSelection } = currentService;
        if (selectionSignature(selection) === selectionSignature(initialSelections[key])) {
            setStep((current) => current + 1);
            return;
        }
        if (!service.service_id) {
            setError("This service is missing its appointment service ID and cannot be edited.");
            return;
        }

        setSavingEdit(true);
        setError("");
        try {
            await editAppointment(appointment.id, [{
                service_id: Number(service.service_id),
                appointment_attributes: attributeSelection,
            }]);
            setInitialSelections((current) => ({ ...current, [key]: { ...selection } }));
            setEditedServiceKeys((current) => new Set(current).add(key));
            setStep((current) => current + 1);
        } catch (requestError) {
            setError(editErrorMessage(requestError));
        } finally {
            setSavingEdit(false);
        }
    }

    async function saveTipAndContinue() {
        if (tip !== null && (tipAmount !== savedTip || tip.type !== "none")) {
            const type = tip.type === "none" ? "none" : tip.type === "percent" ? "standard" : "custom";
            const value = tip.type === "percent" ? Number(tip.value) : tipAmount;

            setSavingTip(true);
            setError("");
            try {
                await addTipToAppointment(appointment.id, type, value);
                setSavedTip(tipAmount);
            } catch (requestError) {
                setError(requestError.message);
                setSavingTip(false);
                return;
            }
            setSavingTip(false);
        }

        setStep((current) => current + 1);
    }

    function buildRequest() {
        if (mode === "cancel") return { type: "cancel", appointment_id: appointment.id };

        const request = {
            type: mode,
            appointment_id: appointment.id,
            services: pricedServices.map(({ service, valueIDs }) => ({
                service_id: service.service_id,
                product_id: service.product_id,
                attribute_value_ids: valueIDs,
            })),
        };
        return mode === "card" ? { ...request, tip_cents: tipAmount } : request;
    }

    async function submit() {
        if (mode !== "cancel" && overpayment > 0) {
            setError("This appointment is overpaid. Review the refund before completing it.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            await onSubmit(buildRequest());
            setDone(true);
            onCompleted?.();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSubmitting(false);
        }
    }

    function confirmLabel() {
        if (submitting) return "Processing...";
        if (mode === "cancel") return "Cancel appointment";
        if (mode === "cash") return "Mark paid in cash";
        return amountDue > 0 ? `Charge ${formatPrice(amountDue)}` : "Complete appointment";
    }

    function actionButtons() {
        if (done) return [{ key: "done", text: "Done", className: styles.footerAction, onClick: onClose }];

        const back = mode
            ? { key: "back", text: "Back", color: "light", className: styles.footerAction, disabled: submitting || savingEdit || savingTip, onClick: goBack }
            : { key: "close", text: "Close", color: "light", className: styles.footerAction, onClick: handleClose };

        if (currentStep?.type === "service") return [back, {
            key: "next",
            text: savingEdit ? "Saving changes..." : "Continue",
            className: styles.footerAction,
            disabled: loading || savingEdit || !currentService?.complete,
            onClick: saveServiceAndContinue,
        }];
        if (currentStep?.type === "tip") return [back, {
            key: "next",
            text: savingTip ? "Saving tip..." : "Continue",
            className: styles.footerAction,
            disabled: savingTip || !isTipValid(tip),
            onClick: saveTipAndContinue,
        }];
        if (currentStep?.type === "review") {
            return [back, {
                key: "confirm",
                text: confirmLabel(),
                className: styles.footerAction,
                color: mode === "cancel" ? "danger" : "accent",
                disabled: submitting || (mode !== "cancel" && (deposit === null || overpayment > 0)),
                onClick: submit,
            }];
        }
        return [back];
    }

    function renderServiceStep() {
        if (loading) return <p className={styles.muted}>Loading service options...</p>;

        const { key, product, selection, name, price, service } = currentService;
        return (
            <div className={styles.services}>
                <div className={styles.row}>
                    <strong>{name}</strong>
                    <span>{formatPrice(price)}</span>
                </div>
                {product ? (
                    <ServiceBuilder
                        product={product}
                        selections={selection}
                        onChange={(next) => setSelections((current) => ({ ...current, [key]: next }))}
                    />
                ) : (
                    <>
                        <p className={styles.muted}>This service is no longer in the catalog, so its options can't be changed.</p>
                        {(service.attributes || []).map((attribute) => (
                            <div key={attribute.attribute_id} className={styles.row}>
                                <span>{attribute.name}</span>
                                <strong>{attribute.value}</strong>
                            </div>
                        ))}
                    </>
                )}
            </div>
        );
    }

    if (!isOpen || !appointment) return null;

    const customerName = appointment.customer?.name || appointment.customer_name || "customer";
    const title = done ? "All set" : mode === "cancel" ? "Cancel appointment" : "Complete appointment";
    const appointmentDate = resolveAppointmentDateKey(appointment);

    return (
        <Modal2
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            actionButtons={actionButtons()}
            errorMessage={error}
            styles={{
                panel: {
                    height: "clamp(420px, 90vh, 980px)",
                    width: "clamp(800px, 75vw, 1200px)",
                },
            }}
        >
            <LoadingScreen open={savingEdit || savingTip || submitting}
                message={submitting
                    ? mode === "card" ? "Collecting payment..." : mode === "cash" ? "Completing appointment..." : "Cancelling appointment..."
                    : savingEdit ? "Saving service options..." : "Saving tip..."} />
            <div className={styles.modal}>
                <div className={styles.splitLayout}>
                    <section className={styles.workflow}>
                        <div className={styles.workflowHeader}>
                            <div>
                                <p className={styles.eyebrow}>Appointment</p>
                                <p className={styles.appointmentContext}>
                                    <strong>{customerName}</strong>
                                    <span>{appointmentDate} · {formatTime(appointment.start_time)}</span>
                                </p>
                            </div>
                            {mode && !done ? <span className={styles.stepCount}>Step {step + 1} of {steps.length}</span> : null}
                        </div>

                        {mode && !done && steps.length > 1 ? (
                            <div className={styles.progress} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
                                {steps.map(({ label }, index) => (
                                    <div key={label} className={`${styles.progressItem} ${index <= step ? styles.progressActive : ""}`}>
                                        <span>{index + 1}</span>{label}
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        <div className={styles.workflowContent}>
                            {done ? (
                                <div className={styles.doneState}>
                                    <span className={styles.doneMark} aria-hidden="true">✓</span>
                                    <p className={styles.done}>{DONE_MESSAGES[mode]}</p>
                                </div>
                            ) : !mode ? (
                                <div className={styles.actions}>
                                    <div className={styles.sectionIntro}>
                                        <h2>How was this appointment paid?</h2>
                                        <p>Choose how you’d like to settle the remaining balance.</p>
                                    </div>
                                    <button type="button" className={styles.action} onClick={() => chooseMode("card")}>
                                        <strong>Charge card on file</strong>
                                        <span>Review service options, add a tip, and charge the remaining balance.</span>
                                    </button>
                                    <button type="button" className={styles.action} onClick={() => chooseMode("cash")}>
                                        <strong>Paid in cash</strong>
                                        <span>Review service options and record the remaining balance as cash.</span>
                                    </button>
                                    <button type="button" className={styles.action} disabled={!canCancel} onClick={() => chooseMode("cancel")}>
                                        <strong>Cancel appointment</strong>
                                        <span>{canCancel ? "Available because this appointment is more than 24 hours away." : "Available only more than 24 hours before the appointment."}</span>
                                    </button>
                                </div>
                            ) : currentStep.type === "service" ? (
                                renderServiceStep()
                            ) : currentStep.type === "tip" ? (
                                <TipStep totalCents={total} tip={tip} onChange={setTip} />
                            ) : mode === "cancel" ? (
                                <div className={styles.cancelReview}>
                                    <h2>Confirm cancellation</h2>
                                    <p>Cancel {customerName}&apos;s appointment on {appointmentDate} at {formatTime(appointment.start_time)}? This can&apos;t be undone.</p>
                                </div>
                            ) : (
                                <div className={styles.review}>
                                    <div className={styles.sectionIntro}>
                                        <h2>Review and confirm</h2>
                                        <p>Check the appointment summary before completing payment.</p>
                                    </div>
                                    {pricedServices.map(({ key, name, price, labels }) => (
                                        <div key={key} className={styles.reviewService}>
                                            <div className={styles.row}>
                                                <strong>{name}</strong>
                                                <span>{formatPrice(price)}</span>
                                            </div>
                                            {labels.map((label) => (
                                                <small key={label} className={styles.muted}>{label}</small>
                                            ))}
                                        </div>
                                    ))}
                                    {total !== originalTotal ? (
                                        <div className={`${styles.row} ${styles.muted}`}><span>Originally booked</span><span>{formatPrice(originalTotal)}</span></div>
                                    ) : null}
                                    <p className={styles.muted}>The current balance and payment breakdown are shown in the summary.</p>
                                </div>
                            )}
                        </div>
                    </section>

                    <aside className={styles.summaryPanel} aria-label="Appointment and payment summary">
                        <div className={styles.summaryHeader}>
                            <p className={styles.eyebrow}>Summary</p>
                            <h2>{customerName}</h2>
                            <p>{appointmentDate} · {formatTime(appointment.start_time)}</p>
                        </div>

                        <div className={styles.summaryServices}>
                            <h3>Services</h3>
                            {loading ? <p className={styles.muted}>Loading service details…</p> : (
                                pricedServices.length ? pricedServices.map(({ key, name, price, labels }) => (
                                    <div className={styles.summaryService} key={key}>
                                        <div className={styles.row}>
                                            <strong>{name}</strong>
                                            <span>{formatPrice(price)}</span>
                                        </div>
                                        {labels.length > 0 ? (
                                            <ul>
                                                {labels.map((label) => <li key={label}>{label}</li>)}
                                            </ul>
                                        ) : null}
                                    </div>
                                )) : <p className={styles.muted}>No services listed.</p>
                            )}
                        </div>

                        <div className={styles.summaryTotals}>
                            {discountPercentage > 0 && (
                                <div className={styles.summaryRow}><span>Discount ({discountPercentage}%)</span><span>−{formatPrice(subtotal - total)}</span></div>
                            )}
                            {total !== originalTotal ? (
                                <div className={styles.summaryRow}><span>Originally booked</span><span>{formatPrice(originalTotal)}</span></div>
                            ) : null}
                            <div className={styles.summaryRow}><span>Service total</span><strong>{formatPrice(total)}</strong></div>
                            <div className={styles.summaryRow}><span>Deposit paid</span><span>{deposit === null ? "—" : `−${formatPrice(deposit)}`}</span></div>
                            {overpayment > 0 && <p role="alert">Overpaid by {formatPrice(overpayment)}. Refund review is required before completion.</p>}
                            <div className={styles.summaryRow}><span>Remaining balance</span><span>{remaining > 0 ? formatPrice(remaining) : formatPrice(0)}</span></div>
                            {mode === "card" || savedTip > 0 ? (
                                <div className={styles.summaryRow}><span>Tip</span><span>{formatPrice(tipAmount)}</span></div>
                            ) : null}
                            {mode === "cancel" ? (
                                <div className={styles.noChargeNote}>No payment will be collected.</div>
                            ) : (
                                <div className={styles.summaryGrandTotal}>
                                    <span>{mode === "card" ? "Charge to card" : mode === "cash" ? "Collect in cash" : "Amount due"}</span>
                                    <strong>{formatPrice(amountDue)}</strong>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </Modal2>
    );
}

export default CompleteAppointmentModal;
